import { Fragment } from "react";
import type { ReactNode } from "react";
import { Link as MuiLink } from "@mui/material";
import { formatPhoneForTel } from "../utils/contactLinks";

const URL_TOKEN = /(https?:\/\/[^\s<>\]'"()]+)/gi;
const EMAIL_TOKEN = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
const PHONE_TOKEN = /(\+385[\d\s/\-]{5,}\d|0\d[\d\s/\-]{6,}\d)/g;

function interleavePhones(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  const re = new RegExp(PHONE_TOKEN.source, "g");
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) {
      out.push(text.slice(last, m.index));
    }
    const raw = m[1];
    const tel = formatPhoneForTel(raw);
    out.push(
      tel ? (
        <MuiLink key={`${m.index}-tel`} href={`tel:${tel}`}>
          {raw}
        </MuiLink>
      ) : (
        raw
      ),
    );
    last = m.index + raw.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out.length ? out : [text];
}

function splitEmailsAndUrls(segment: string): ReactNode[] {
  const combined = new RegExp(
    `${URL_TOKEN.source}|${EMAIL_TOKEN.source}`,
    "gi",
  );
  const nodes: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = combined.exec(segment)) !== null) {
    if (m.index > last) {
      nodes.push(...interleavePhones(segment.slice(last, m.index)));
    }
    const match = m[0];
    if (/^https?:\/\//i.test(match)) {
      nodes.push(
        <MuiLink
          key={`${m.index}-url`}
          href={match}
          target="_blank"
          rel="noopener noreferrer"
        >
          {match}
        </MuiLink>,
      );
    } else {
      nodes.push(
        <MuiLink key={`${m.index}-mail`} href={`mailto:${match}`}>
          {match}
        </MuiLink>,
      );
    }
    last = m.index + match.length;
  }
  if (last < segment.length) {
    nodes.push(...interleavePhones(segment.slice(last)));
  }
  return nodes.length ? nodes : interleavePhones(segment);
}

interface SmartInlineTextProps {
  text: string;
}

/**
 * Plain text with auto-linked URLs, email, and phone (tel:), similar in spirit to ph-zgss contact icons.
 */
export function SmartInlineText({ text }: SmartInlineTextProps) {
  const lines = text.split("\n");
  return (
    <>
      {lines.map((line, li) => (
        <Fragment key={li}>
          {li > 0 ? "\n" : null}
          <span>
            {splitEmailsAndUrls(line).map((node, i) => (
              <Fragment key={i}>{node}</Fragment>
            ))}
          </span>
        </Fragment>
      ))}
    </>
  );
}
